import { z } from 'zod';

// ---- Sugestões e suporte (tickets) ---------------------------------------------------------------

export const SUPPORT_KINDS = ['IDEA', 'PROBLEM'] as const;
export type SupportKind = (typeof SUPPORT_KINDS)[number];

export const SUPPORT_STATUSES = ['OPEN', 'IN_REVIEW', 'RESOLVED', 'CLOSED'] as const;
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export const SUPPORT_KIND_LABEL: Record<SupportKind, string> = { IDEA: 'Sugestão', PROBLEM: 'Problema' };
export const SUPPORT_STATUS_LABEL: Record<SupportStatus, string> = { OPEN: 'Aberto', IN_REVIEW: 'Em análise', RESOLVED: 'Resolvido', CLOSED: 'Fechado' };

export const SUPPORT_SUBJECT_MIN = 3;
export const SUPPORT_SUBJECT_MAX = 120;
/** First message of a ticket: enough to explain the idea or the problem. */
export const SUPPORT_FIRST_MESSAGE_MIN = 10;
export const SUPPORT_MESSAGE_MAX = 4000;
/** Tickets a user may have waiting at once (Aberto + Em análise). */
export const SUPPORT_MAX_ACTIVE = 10;

/** A closed ticket takes no more messages: the user opens a new one. */
export const supportAcceptsReplies = (status: SupportStatus) => status !== 'CLOSED';

/** Postgres text rejects NUL bytes (it would be a 500): they are dropped before validation. */
const clean = (v: unknown) => (typeof v === 'string' ? v.replace(/\u0000/g, '') : v);

const messageText = (min: number, minMessage: string) =>
  z.preprocess(clean, z.string().trim().min(min, minMessage).max(SUPPORT_MESSAGE_MAX, `A mensagem pode ter até ${SUPPORT_MESSAGE_MAX} caracteres.`));

export const supportTicketCreateSchema = z.object({
  kind: z.enum(SUPPORT_KINDS, { error: 'Escolha o tipo do ticket.' }),
  // One line: line breaks and runs of spaces become a single space.
  subject: z.preprocess(
    (v) => (typeof v === 'string' ? v.replace(/[\u0000\s]+/g, ' ') : v),
    z.string().trim().min(SUPPORT_SUBJECT_MIN, `O assunto precisa de pelo menos ${SUPPORT_SUBJECT_MIN} caracteres.`).max(SUPPORT_SUBJECT_MAX, `O assunto pode ter até ${SUPPORT_SUBJECT_MAX} caracteres.`),
  ),
  message: messageText(SUPPORT_FIRST_MESSAGE_MIN, `Explique com pelo menos ${SUPPORT_FIRST_MESSAGE_MIN} caracteres.`),
});

export const supportReplySchema = z.object({ body: messageText(1, 'Escreva uma mensagem.') });

export const supportStatusSchema = z.object({ status: z.enum(SUPPORT_STATUSES) });

export interface SupportMessageDTO {
  id: string;
  fromAdmin: boolean;
  body: string;
  createdAt: string;
  /** Admin view only: who wrote it. */
  authorName?: string | null;
  /** Admin view only: written by the admin who is reading. */
  mine?: boolean;
}

export interface SupportTicketSummaryDTO {
  id: string;
  kind: SupportKind;
  subject: string;
  status: SupportStatus;
  createdAt: string;
  lastMessageAt: string;
  lastFromAdmin: boolean;
  /** Messages from the other side not opened yet. */
  unread: number;
  messageCount: number;
  /** Start of the last message. */
  preview: string;
}

export interface SupportTicketDTO extends SupportTicketSummaryDTO {
  messages: SupportMessageDTO[];
  /** Sent back as `?v=` while polling: nothing changed → `{ unchanged: true }` instead of the whole conversation. */
  version: string;
}

export interface SupportUnchangedDTO {
  unchanged: true;
}

export interface SupportTicketUserDTO {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  affiliate: boolean;
}

export interface AdminSupportTicketSummaryDTO extends SupportTicketSummaryDTO {
  user: SupportTicketUserDTO;
}

export interface AdminSupportTicketDTO extends AdminSupportTicketSummaryDTO {
  messages: SupportMessageDTO[];
  version: string;
}

export interface AdminSupportListDTO {
  items: AdminSupportTicketSummaryDTO[];
  /** Per status, with the type and search filters applied. */
  counts: Record<SupportStatus | 'ALL', number>;
  /** Open conversations whose last message is the user's. */
  waiting: number;
  nextSkip: number | null;
}
